/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import produce from 'immer';
import {cloneDeep} from 'lodash';
import {toAccountIdMap} from '@ion/core/src/entities/Accounts';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {Account, AccountLink} from '@ion/core/src/types/entities';
import {sortNestedAccounts} from '@ion/core/src/utils/SortUtils';
import {
  GLOBAL_GROUP_INITIALIZED,
  REFRESH_ACCOUNT_HIERARCHY,
  SET_CURRENT_ACCOUNT,
  SET_CURRENT_WEBMAPID,
  SET_GLOBAL_GROUP,
  SET_LOADING_STATE,
  TOGGLE_GLOBAL_GROUP_CHOOSER
} from '../actions/global-group-chooser';
import {APPLICATION_INITIALIZATION_COMPLETE} from '../actions/initialization';
import {LOGIN_SUCCEEDED} from '../actions/login';

export interface GlobalGroupState {
  groupChooser: {
    isViewing: boolean;
  };
  accountMap: {
    [key: number]: AccountLink
  };
  accountHierarchy: AccountLink[];
  globalGroup: AccountLink;
  globalGroupId: number;
  currentAccount: Account;
  loading: boolean;
  globalGroupHierarchy: AccountLink[];
  ancestorAccountId: AccountLink[];
  webMapId?: string;
  isInitialized?: boolean;
}

// defines the initial state for the reducer ...
export const initialState: GlobalGroupState = {
  groupChooser : {
    isViewing : false
  },
  accountMap           : null,
  accountHierarchy     : null,
  globalGroup          : null,
  globalGroupId        : null,
  globalGroupHierarchy : null,
  ancestorAccountId    : null,
  currentAccount       : null,
  loading              : false,
  webMapId             : null,
  isInitialized        : false
};

// defines this reducers reducer functions ...
const reducers = {
  [TOGGLE_GLOBAL_GROUP_CHOOSER] : ({groupChooser}: GlobalGroupState, {value}) => {
    groupChooser.isViewing = value;
  },
  [LOGIN_SUCCEEDED] : (draft: GlobalGroupState, {account}) => {
    draft.currentAccount = account;
  },
  [APPLICATION_INITIALIZATION_COMPLETE] : (
    draft: GlobalGroupState, {accounts, user, canUpdateGroupId = true}
  ) => {
    const accountsMap = toAccountIdMap(accounts.concat({...user.account, subAccounts: accounts}));
    const hierarchy = [{
      ...user.account,
      subAccounts : sortNestedAccounts({accounts: cloneDeep(accounts)})
    }];
    const account           = hierarchy[0];
    draft.accountMap        = accountsMap;
    draft.accountHierarchy  = hierarchy;
    if (canUpdateGroupId) {
      draft.globalGroup       = account;
      draft.globalGroupId     = entityId(account);
      const selectedAccount   = accountsMap[entityId(account)];
      draft.globalGroupHierarchy = [selectedAccount];
    }
  },
  [SET_LOADING_STATE] : (draft: GlobalGroupState, {group}) => {
    draft.globalGroup    = group;
    draft.globalGroupId  = entityId(group);
    draft.loading        = true;
    draft.currentAccount = null;
  },
  [SET_GLOBAL_GROUP] : (draft: GlobalGroupState, {group, ancestorAccountId}) => {
    const groupId                = entityId(group);
    draft.globalGroup            = group;
    draft.globalGroupId          = groupId;
    draft.groupChooser.isViewing = false;
    const selectedAccount        = draft.accountMap[groupId];
    draft.globalGroupHierarchy   = [selectedAccount];
    draft.ancestorAccountId      = [ancestorAccountId];
  },
  [SET_CURRENT_ACCOUNT] : (draft: GlobalGroupState, {account}) => {
    draft.currentAccount = account;
    draft.loading = false;
  },
  [REFRESH_ACCOUNT_HIERARCHY] : (draft: GlobalGroupState, {accounts}) => {
    draft.accountHierarchy[0].subAccounts = sortNestedAccounts({accounts: cloneDeep(accounts)});
    draft.accountMap                      = toAccountIdMap(draft.accountHierarchy);
  },
  [SET_CURRENT_WEBMAPID] : (draft: GlobalGroupState, {webMapId}) => {
    draft.webMapId = webMapId;
  },
  [GLOBAL_GROUP_INITIALIZED] : (draft: GlobalGroupState) => {
    draft.isInitialized = true;
  }
};

// defines all reducers for actions of interest to the this reducer ...
export default produce((draft: GlobalGroupState = initialState, action) => {
  reducers[action.type]?.(draft, action);
  return draft;
});
